import React, { createContext, useContext, ReactNode } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { useGetOrganizationDetailsQuery } from '@/lib/services/organizationApi';
import { PlanFeatures, OrganizationDetailsData } from '@/lib/api/types';
import { getPageRequiredFeature } from '@/lib/subscriptions/subscriptionConfig';

/**
 * FeaturePermissions - Matches PlanFeatures interface
 * Defines all available features that can be included in a subscription
 */
export type FeaturePermissions = PlanFeatures;

/**
 * SearchPermissions
 * Specific permissions related to search functionality
 * Derived from features + license status
 */
export interface SearchPermissions {
    canSearchEmail: boolean;
    canSearchUsername: boolean;
    canSearchDomain: boolean;
    canSearchIP: boolean;
    canAccessCorporateRecords: boolean;
    canAccessThirdPartyRecords: boolean;
}

/**
 * UnmaskingPermissions
 * Permissions related to unmasking data
 */
export interface UnmaskingPermissions {
    available: number | string;
    used: number;
    canUnmask: boolean;
}

/**
 * Permissions
 * All permission data for the current user
 */
export interface Permissions {
    features: FeaturePermissions;
    search: SearchPermissions;
    unmasking: UnmaskingPermissions;
    hasValidLicense: boolean;
    isLoading: boolean;
    isError: boolean;
}

/**
 * FeatureAccessContextType
 * Shape of the feature access context
 */
export interface FeatureAccessContextType {
    permissions: Permissions;
    hasFeature: (feature: keyof FeaturePermissions) => boolean;
    canAccessPage: (page: string) => boolean;
    getSearchPermissions: () => SearchPermissions;
}

const FeatureAccessContext = createContext<FeatureAccessContextType | undefined>(undefined);

export interface FeatureAccessProviderProps {
    children: ReactNode;
}

/**
 * FeatureAccessProvider
 * Provides feature access checking throughout the app
 *
 * Uses RTK Query to fetch organization details
 * Dynamically determines permissions based on API response
 */
export const FeatureAccessProvider: React.FC<FeatureAccessProviderProps> = ({ children }) => {
    const { isLoaded: authLoaded, isSignedIn } = useAuth();

    // RTK Query hook to fetch organization details
    // Skip the query until auth is ready and user is signed in
    const {
        data: orgDetailsResponse,
        isLoading: queryLoading,
        isError,
    } = useGetOrganizationDetailsQuery(undefined, {
        skip: !authLoaded || !isSignedIn,
    });

    // Extract the data from the response
    const orgDetails = orgDetailsResponse?.data;

    /**
     * mapPermissions
     * Converts API response to permission object
     * Handles all loading/error states
     */
    const mapPermissions = (data?: OrganizationDetailsData): Permissions => {
        // 1. Handle auth not loaded yet
        if (!authLoaded) {
            return {
                features: {
                    corporateRecords: false,
                    thirdPartyRecords: false,
                    emailRecords: false,
                    realTimeAlerting: false,
                    gradientTracing: false, // ✅ Restored missing key
                    incidentSaving: false,
                    threatProfiling: false,
                    threatFeeds: false,
                    systemInformation: false,
                    ipAddresses: false,
                    telegramIntelligence: false
                },
                search: {
                    canSearchEmail: false,
                    canSearchUsername: false,
                    canSearchDomain: false,
                    canSearchIP: false,
                    canAccessCorporateRecords: false,
                    canAccessThirdPartyRecords: false,
                },
                unmasking: {
                    available: 0,
                    used: 0,
                    canUnmask: false,
                },
                hasValidLicense: true,
                isLoading: true,
                isError: false,
            };
        }

        // 2. Handle not signed in
        if (!isSignedIn) {
            return {
                features: {
                    corporateRecords: false,
                    thirdPartyRecords: false,
                    emailRecords: false,
                    realTimeAlerting: false,
                    gradientTracing: false, // ✅ Restored missing key
                    incidentSaving: false,
                    threatProfiling: false,
                    threatFeeds: false,
                    systemInformation: false,
                    ipAddresses: false,
                    telegramIntelligence: false
                },
                search: {
                    canSearchEmail: false,
                    canSearchUsername: false,
                    canSearchDomain: false,
                    canSearchIP: false,
                    canAccessCorporateRecords: false,
                    canAccessThirdPartyRecords: false,
                },
                unmasking: {
                    available: 0,
                    used: 0,
                    canUnmask: false,
                },
                hasValidLicense: false,
                isLoading: false,
                isError: false,
            };
        }

        // 3. Handle RTK query loading
        if (queryLoading) {
            return {
                features: {
                    corporateRecords: false,
                    thirdPartyRecords: false,
                    emailRecords: false,
                    realTimeAlerting: false,
                    gradientTracing: false, // ✅ Restored missing key
                    incidentSaving: false,
                    threatProfiling: false,
                    threatFeeds: false,
                    systemInformation: false,
                    ipAddresses: false,
                    telegramIntelligence: false
                },
                search: {
                    canSearchEmail: false,
                    canSearchUsername: false,
                    canSearchDomain: false,
                    canSearchIP: false,
                    canAccessCorporateRecords: false,
                    canAccessThirdPartyRecords: false,
                },
                unmasking: {
                    available: 0,
                    used: 0,
                    canUnmask: false,
                },
                hasValidLicense: true, // Assume valid during loading
                isLoading: true,
                isError: false,
            };
        }

        // 4. Handle RTK query error
        if (isError) {
            return {
                features: {
                    corporateRecords: false,
                    thirdPartyRecords: false,
                    emailRecords: false,
                    realTimeAlerting: false,
                    gradientTracing: false, // ✅ Restored missing key
                    incidentSaving: false,
                    threatProfiling: false,
                    threatFeeds: false,
                    systemInformation: false,
                    ipAddresses: false,
                    telegramIntelligence: false
                },
                search: {
                    canSearchEmail: false,
                    canSearchUsername: false,
                    canSearchDomain: false,
                    canSearchIP: false,
                    canAccessCorporateRecords: false,
                    canAccessThirdPartyRecords: false,
                },
                unmasking: {
                    available: 0,
                    used: 0,
                    canUnmask: false,
                },
                hasValidLicense: false,
                isLoading: false,
                isError: true,
            };
        }

        // 5. Handle missing data
        if (!data) {
            return {
                features: {
                    corporateRecords: false,
                    thirdPartyRecords: false,
                    emailRecords: false,
                    realTimeAlerting: false,
                    gradientTracing: false, // ✅ Restored missing key
                    incidentSaving: false,
                    threatProfiling: false,
                    threatFeeds: false,
                    systemInformation: false,
                    ipAddresses: false,
                    telegramIntelligence: false
                },
                search: {
                    canSearchEmail: false,
                    canSearchUsername: false,
                    canSearchDomain: false,
                    canSearchIP: false,
                    canAccessCorporateRecords: false,
                    canAccessThirdPartyRecords: false,
                },
                unmasking: {
                    available: 0,
                    used: 0,
                    canUnmask: false,
                },
                hasValidLicense: false,
                isLoading: false,
                isError: true,
            };
        }

        // 6. PROCESS VALID DATA
        if (!data.planDetails || !data.planDetails.features) {
            return {
                features: {
                    corporateRecords: false,
                    thirdPartyRecords: false,
                    emailRecords: false,
                    realTimeAlerting: false,
                    gradientTracing: false, // ✅ Restored missing key
                    incidentSaving: false,
                    threatProfiling: false,
                    threatFeeds: false,
                    systemInformation: false,
                    ipAddresses: false,
                    telegramIntelligence: false
                },
                search: {
                    canSearchEmail: false,
                    canSearchUsername: false,
                    canSearchDomain: false,
                    canSearchIP: false,
                    canAccessCorporateRecords: false,
                    canAccessThirdPartyRecords: false,
                },
                unmasking: {
                    available: 0,
                    used: 0,
                    canUnmask: false,
                },
                hasValidLicense: false,
                isLoading: false,
                isError: true,
            };
        }

        let features = data.planDetails.features;
        const unmasking = data.planDetails.unmasking || {
            used: 0,
            monthlyLimit: 0,
            remaining: 0,
            isUnlimited: false
        };

        if (data.planDetails.planName === "Informant") {
            features = {...features, telegramIntelligence: true}
        }

        // Check if license is valid (not expired)
        let hasValidLicense = false;
        if (data.license && data.license.expiresAt) {
            const licenseExpiry = new Date(data.license.expiresAt);
            const now = new Date();
            hasValidLicense = licenseExpiry > now;
        }

        // Build search permissions based on features
        return {
            features,
            search: {
                canSearchEmail: features.emailRecords && hasValidLicense,
                canSearchUsername: (features.corporateRecords || features.thirdPartyRecords) && hasValidLicense,
                canSearchDomain: features.systemInformation && hasValidLicense,
                canSearchIP: features.ipAddresses && hasValidLicense,
                canAccessCorporateRecords: features.corporateRecords && hasValidLicense,
                canAccessThirdPartyRecords: features.thirdPartyRecords && hasValidLicense,
            },
            unmasking: {
                available: unmasking.remaining,
                used: unmasking.used,
                canUnmask: unmasking.remaining > 0 && !unmasking.isUnlimited && hasValidLicense,
            },
            hasValidLicense,
            isLoading: false,
            isError: false,
        };
    };

    const permissions = mapPermissions(orgDetails);

    /**
     * hasFeature
     * Check if user has a specific feature
     * Returns true during loading to prevent lockouts
     */
    const hasFeature = (feature: keyof FeaturePermissions): boolean => {
        if (permissions.isLoading) return true;
        return permissions.features[feature] && permissions.hasValidLicense;
    };

    /**
     * canAccessPage
     * Check if user can access a page
     */
    const canAccessPage = (page: string): boolean => {
        if (permissions.isLoading) return true;

        const requiredFeature = getPageRequiredFeature(page);

        if (requiredFeature === null) return true;
        if (requiredFeature === undefined) return true;

        return hasFeature(requiredFeature);
    };

    /**
     * getSearchPermissions
     * Get search-specific permissions
     */
    const getSearchPermissions = (): SearchPermissions => {
        return permissions.search;
    };

    const contextValue: FeatureAccessContextType = {
        permissions,
        hasFeature,
        canAccessPage,
        getSearchPermissions,
    };

    return (
        <FeatureAccessContext.Provider value={contextValue}>
            {children}
        </FeatureAccessContext.Provider>
    );
};

export const useFeatureAccess = (): FeatureAccessContextType => {
    const context = useContext(FeatureAccessContext);
    if (!context) {
        throw new Error('useFeatureAccess must be used within a FeatureAccessProvider');
    }
    return context;
};

export const useFeature = (feature: keyof FeaturePermissions) => {
    const { hasFeature } = useFeatureAccess();
    return hasFeature(feature);
};

export const usePageAccess = (page: string) => {
    const { canAccessPage } = useFeatureAccess();
    return canAccessPage(page);
};